import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const HamptaPass = () => {
  const tourData = {
    name: "Hampta Pass Trek",
    duration: "04 Nights 05 Days",
    price: "₹ 4,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935183/khgpbg3twluagvqaqwi4.avif",
    altitude: "14,200 ft",
    difficulty: "Easy to Moderate",
    region: "Himachal Pradesh",
    highlights: ["Cross-country trek", "Alpine meadows", "River crossings"],
    bestTime: "June to October",
    description: "The Hampta Pass Trek is a beautiful cross-country trek that takes you from the lush green valleys of Kullu to the arid landscapes of Lahaul. This easy to moderate trek offers stunning views of alpine meadows, snow-capped peaks, and the famous Chandratal Lake."
  };

  return (
    <>
      <Helmet>
        <title>Hampta Pass Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Hampta Pass Trek in Himachal Pradesh. Cross-country trek from Kullu to Lahaul with stunning alpine meadows and Chandratal Lake." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              A beautiful cross-country trek from Kullu to Lahaul
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek offers a unique opportunity to experience the dramatic contrast between the 
                    green valleys of Kullu and the stark beauty of Lahaul. The Hampta Pass serves as a 
                    gateway between these two distinct landscapes, offering trekkers an unforgettable 
                    journey through some of the most beautiful terrain in the Himalayas.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Cross-country trek experience</li>
                    <li>Alpine meadows and valleys</li>
                    <li>River crossings and waterfalls</li>
                    <li>Chandratal Lake visit</li>
                    <li>Snow-capped peaks views</li>
                    <li>Dramatic landscape contrast</li>
                    <li>Photography opportunities</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Manali to Chika trek</h4>
                    <ul>
                      <li>After we have finished our meal in the morning, we will begin the day's journey with a breathtaking drive to Jobara.</li>
                      <li>The trek to Chikka is scheduled to take around two to three hours to accomplish.</li>
                      <li>The campground is located after passing through a short woodland section and then two wooden bridges before you arrive at the campsite.</li>
                      <li>In addition to its stunning waterfall and large granite boulders, Chikka provides visitors with an unforgettable opportunity to camp along the riverbed.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: From Chika to Balu ka ghera trek</h4>
                    <ul>
                      <li>The trek to Balu Ka Ghera will begin after we have finished our meal in the morning.</li>
                      <li>In the beginning of the trek, you will climb a few rock boulders as you make your way through the right bank of the Hamta River.</li>
                      <li>During the trek, there is a water stream that must be across, and then there is a three-hour walk to Balu Ka Ghera that must be completed.</li>
                      <li>After catching your first view of the towering snow-capped peaks of the Himalayas, you will have the opportunity to experience the genuine magnificence of the Himalayas.</li>
                      <li>These mountains are part of the Dhauladhar ranges, which are part of the outer Himalayan chain.</li>
                      <li>You will be able to take pleasure in the picture-perfect beauty that you will encounter as you stroll through flowery meadows and valleys.</li>
                      <li>Having a leisurely evening.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: From Balu ka ghera to Sheagoru via Hampta Pass</h4>
                    <ul>
                      <li>The ever-changing Himalayan scenery will be yours to see as we set off on our trek first thing in the morning after breakfast.</li>
                      <li>The trail's gentle inclines make it ideal for first-timers, and you may learn a lot about the valley and its inhabitants from the shepherds you meet along the way.</li>
                      <li>If you follow these paths, you will eventually reach the breathtaking Hampta Valley.</li>
                      <li>We depart from the Hampta settlement and go via the Lahaul Valley.</li>
                      <li>Downhill and steep it is on the path. Most of the final km leading up to the campground is level.</li>
                      <li>Although the winds make Shea Goru, which translates as "Cold Street" in the local language, quite chilly, it is also the most picturesque campground on the Hampta Pass hike.</li>
                      <li>Staying the night at Sheagoru for camping.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: From Sheagoru to Chatru trek to Chandratal and back to Chatru</h4>
                    <ul>
                      <li>After we have finished our breakfast, we will begin our trek to Chatru, which will take around three hours.</li>
                      <li>Falling through the Hampta Valley on the way down.</li>
                      <li>In front of you, the majestic Pir Panjal mountains, which are located inside the inner Himalayan circle, come into view.</li>
                      <li>As soon as you arrive at Chhatru, take some time to unwind, take in the breathtaking scenery and have lunch.</li>
                      <li>After that, we will move ahead for Chandratal, which is located at an elevation of 14,000 feet, by driving over the gorgeous trails and passes of Chhatru.</li>
                      <li>After some time spending in Chandra Tal, we will get back to chatru for dinner and overnight stay.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: From Chatru to Manali</h4>
                    <ul>
                      <li>We will begin our journey to Manali via the Atal Tunnel once we have had our meal in the morning.</li>
                      <li>In order to reach Manali, we will arrive at 1300 hours.</li>
                      <li>Check in to the hotel and take some rest.</li>
                      <li>Explore the mall road again in the evening and today is the last day of your Hampta pass Trekking journey.</li>
                      <li>Say goodbye to mountains and new friends, take best memories to home.</li>
                      <li>Board the vehicle to get back to your homes.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Pick and Drop Transfers from/to Manali</li>
                    <li>All meals are included with proper hygiene parameters</li>
                    <li>Triple/double sharing tent accommodations are available</li>
                    <li>Guest house with triple occupancy</li>
                    <li>During the trek, there will be morning and evening tea with a small refreshment</li>
                    <li>All tolls and forest fees</li>
                    <li>Trekking gears (tents, sleeping bags, ropes, gaiters, crampons etc.)</li>
                    <li>First aid medical kit</li>
                    <li>Professional trek leader and support staff</li>
                    <li>Safety equipment and communication devices</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Anything that has not been covered in the inclusions mentioned above</li>
                    <li>Porters or mules to carry your luggage. The cost per bag (10/12 kg) is 300 per day</li>
                    <li>Any kind of personal expenses</li>
                    <li>Any kind of personal medication</li>
                    <li>Transportation to and from Manali</li>
                    <li>Personal trekking gear and equipment</li>
                    <li>Any additional expenses due to weather conditions or unforeseen circumstances</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>14,200 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>26 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy to Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>June to October</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Manali)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 4,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 5,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 5,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Manali:</strong> Manali bus stand between 09:00 AM to 10:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Cross-country trek experience</li>
                    <li>Alpine meadows and valleys</li>
                    <li>River crossings and waterfalls</li>
                    <li>Chandratal Lake visit</li>
                    <li>Snow-capped peaks views</li>
                    <li>Dramatic landscape contrast</li>
                    <li>Photography opportunities</li>
                    <li>Professional trek leader and support staff</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default HamptaPass;
